<template>
  <div
    :class="[
      'retailer-search-filter-fi',
      theme ? `retailer-search-filter-fi--${theme}` : null,
      navigationActive ? 'retailer-search-filter-fi--navigation-active' : null,
    ]"
  >
    <retailer-search-location-fi
      class="retailer-search-filter-fi__search"
      :autocomplete-options
      :loc="location"
      :location-error
      @location-changed="locationChanged"
      @user-location-error="(val) => emit('user-location-error', val)"
    />

    <div class="retailer-search-filter-fi__range">
      <select-input-fi
        class="retailer-search-filter-fi__range__select"
        :label="$dict.get('dealer-Locator.Circle-Entry')"
        :value="selectRange"
        :options="ranges"
        large-from="md"
        light
        @input="rangeChanged"
        @clear-selection="clearRange"
      />
    </div>

    <div class="retailer-search-filter-fi__wrapper">
      <div class="retailer-search-filter-fi__retailer-name">
        <text-input-fi
          v-model.trim="retailerNameModel"
          class="retailer-search-filter-fi__retailer-name__input"
          :label="$dict.get('dealer-Locator.Dealer-Name')"
          icon="search"
          large-from="md"
          light
          clear
          clear-icon="close"
          @icon-clicked="(val) => retailerNameChanged(val, true)"
          @keyup-enter="(val) => retailerNameChanged(val, true)"
          @clear="retailerNameChanged"
        />
      </div>

      <div
        v-if="retailerType && retailerType.elements.length > 1"
        class="retailer-search-filter-fi__retailer-type__mobile"
      >
        <retailer-search-type-fi
          :facet="retailerType"
          :retailers-count="retailersCount"
          @retailer-type-changed="typeChanged"
        />
      </div>
    </div>

    <div
      v-if="retailerType && retailerType.elements.length > 1"
      class="retailer-search-filter-fi__retailer-type"
    >
      <retailer-search-type-fi
        :facet="retailerType"
        :retailers-count="retailersCount"
        @retailer-type-changed="typeChanged"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { SelectInputFi, TextInputFi } from 'atoms';
import { debounce } from 'throttle-debounce';
import { computed, ref } from 'vue';

import type { AutocompleteOptions, GeocodedLocation } from '@/googleHelpers';

import RetailerSearchLocationFi from './retailer-search-location-fi/RetailerSearchLocationFi.vue';
import RetailerSearchTypeFi from './retailer-search-type-fi/RetailerSearchTypeFi.vue';

defineOptions({ name: 'RetailerSearchFilterFi' });

const props = withDefaults(
  defineProps<{
    location?: GeocodedLocation;
    locationError?: boolean;
    retailerName?: string;
    autocompleteOptions?: AutocompleteOptions;
    ranges: any[];
    defaultRange: string;
    theme?: string | null;
    retailerSearchPage?: boolean;
    retailerTypes?: any[];
    retailersCount?: string | null;
    navigationActive?: boolean;
  }>(),
  {
    retailerName: '',
    theme: null,
    retailerSearchPage: false,
    retailerTypes: () => [],
    retailersCount: null,
    navigationActive: false,
    autocompleteOptions: undefined,
    location: undefined,
  },
);

const emit = defineEmits<{
  'location-changed': [payload: any];
  'range-changed': [payload: any];
  'retailer-name-changed': [payload: any];
  'type-changed': [payload: any];
  'user-location-error': [payload: any];
}>();

const internRetailerName = ref('');
const selectRange = ref<string | null>(props.defaultRange);

const debouncedRetailerNameChange = debounce(300, (value: any) => {
  emit('retailer-name-changed', value);
});

const retailerNameChanged = (value: any, forceEmit?: boolean) => {
  if (!value || value.length > 2 || forceEmit) {
    if (forceEmit) {
      emit('retailer-name-changed', value);
    } else {
      debouncedRetailerNameChange(value);
    }
  }
};

const retailerNameModel = computed({
  get() {
    return props.retailerName || internRetailerName.value;
  },
  set(value: string) {
    internRetailerName.value = value;
    retailerNameChanged(value);
  },
});

const retailerType = computed(() => props.retailerTypes?.[0]);

const locationChanged = (location: any) => {
  if (location) {
    prepareDataLayer(location);
  }
  emit('location-changed', location);
};

const rangeChanged = (value: string) => {
  selectRange.value = value;
  emit('range-changed', value);
};

const clearRange = () => {
  selectRange.value = props.defaultRange;
  emit('range-changed', props.defaultRange);
};

const typeChanged = (value: any) => {
  emit('type-changed', value);
};

const prepareDataLayer = (location: any) => {
  if (!location?.description) {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  const pageDetails = window.dataLayer.filter((d: any) => d.pageId)[0];

  if (props.retailerSearchPage) {
    window.dataLayer.push({
      event: 'DealerSearchLocation',
      locationName: location.description,
    });
  } else if (pageDetails) {
    window.dataLayer.push({
      event: 'RCBuyLocalLocation',
      pageId: pageDetails.pageId,
      pageTitle: pageDetails.pageTitle,
      pageUrl: pageDetails.pageUrl,
      pageTemplate: pageDetails.pageTemplate,
      cartItems: pageDetails.cartItems,
      locationName: location.description,
    });
  } else {
    window.dataLayer.push({
      event: 'RCBuyLocalLocation',
      locationName: location.description,
    });
  }
};
</script>

<style scoped src="./retailer-search-filter-fi" />

<style src="./retailer-search-filter-fi-unscoped" />
